import React from "react";
import { gql, useMutation } from "@apollo/client";
import { Form } from "react-final-form";
import { Dialog } from "@syos-music/common";
import {
  SwitchField,
  TextField,
  RatingField,
  Fieldset,
} from "@syos-music/form";
import {
  InstrumentSelectField,
  InstrumentTypeSelectField,
} from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import { Button, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import FileField from "components/form/FileField";
import IntlTextField from "components/form/IntlTextField";
import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

const OriginalsTypeSelectField =
  Product.displayModes.originalsMouthpiece.OriginalsTypeSelectField;

const fragments = {
  review: gql`
    fragment ReviewFormDialog_review on Review {
      id
      published
      pinned

      originals {
        instrument
        instrumentType
        originalsType
      }
      signatureMouthpiece {
        id
      }
      reedsBox {
        instrument
        instrumentType
      }

      avatar {
        id
        ...FileField_file
      }
      name
      rating
      quote {
        en
        fr
      }
    }

    ${FileField.fragments.file}
  `,
};

const MUTATION = gql`
  mutation (
    $id: ID
    $published: Boolean!
    $pinned: Boolean!
    $originals: Review_OriginalsInput
    $signatureMouthpiece: ID
    $reedsBox: Review_ReedsBoxInput
    $avatar: FileInput
    $name: String!
    $rating: Int!
    $quote: IntlStringInput!
  ) {
    UpsertReview(
      id: $id
      published: $published
      pinned: $pinned
      originals: $originals
      signatureMouthpiece: $signatureMouthpiece
      reedsBox: $reedsBox
      avatar: $avatar
      name: $name
      rating: $rating
      quote: $quote
    ) {
      review {
        id

        ...ReviewFormDialog_review
      }
    }
  }

  ${fragments.review}
`;

export default function ReviewFormDialog({ review, ...props }) {
  const initialValues = React.useMemo(() => {
    if (!review?.id) return { published: false, pinned: false };

    return {
      id: review.id,
      published: review.published,
      pinned: review.pinned,
      originals: review.originals,
      signatureMouthpiece: review.signatureMouthpiece?.id,
      reedsBox: review.reedsBox,

      avatar: review.avatar,
      name: review.name,
      rating: review.rating,
      quote: {
        en: review.quote.en,
        fr: review.quote.fr,
      },
    };
  }, [review]);

  const [upsertReview] = useMutation(MUTATION);

  const handleSubmit = React.useCallback(
    (values) => {
      return upsertReview({ variables: values })
        .then(() => {
          props.onClose?.();
        })
        .catch(() => true);
    },
    [upsertReview, props]
  );

  return (
    <Dialog open={Boolean(review)} {...props}>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit, submitting, values }) => (
          <form onSubmit={handleSubmit} autoComplete="off" noValidate>
            <Dialog.Title>Avis client</Dialog.Title>

            <Dialog.Content>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <SwitchField name="published" label="Publié" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SwitchField name="pinned" label="Épinglé (sur la home)" />
                </Grid>

                <Grid item xs={12}>
                  <Fieldset
                    legend="Bec Originals"
                    startCollapsed={!values.originals}
                  >
                    <Grid container>
                      <Grid item xs={4}>
                        <InstrumentSelectField name="originals.instrument" />
                      </Grid>
                      <Grid item xs={4}>
                        {values.originals?.instrument && (
                          <InstrumentTypeSelectField
                            name="originals.instrumentType"
                            instrument={values.originals.instrument}
                          />
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        <OriginalsTypeSelectField name="originals.originalsType" />
                      </Grid>
                    </Grid>
                  </Fieldset>
                </Grid>

                <Grid item xs={12}>
                  <Fieldset
                    legend="Bec Signature"
                    startCollapsed={!values.signatureMouthpiece}
                  >
                    <SignatureMouthpieceSelectField
                      name="signatureMouthpiece"
                      clearable
                    />
                  </Fieldset>
                </Grid>

                <Grid item xs={12}>
                  <Fieldset
                    legend="Boîte d’anches"
                    startCollapsed={!values.reedsBox}
                  >
                    <Grid container>
                      <Grid item xs={6}>
                        <InstrumentSelectField name="reedsBox.instrument" />
                      </Grid>
                      <Grid item xs={6}>
                        {values.reedsBox?.instrument && (
                          <InstrumentTypeSelectField
                            name="reedsBox.instrumentType"
                            instrument={values.reedsBox.instrument}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Fieldset>
                </Grid>

                <Grid item xs={12}>
                  <FileField
                    name="avatar"
                    uploadPath="reviews-avatars"
                    isPublic
                    preview="image"
                    label="Avatar"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField name="name" required label="Nom" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RatingField
                    name="rating"
                    required
                    type="number"
                    label="Rating"
                  />
                </Grid>

                <Grid item xs={12}>
                  <IntlTextField name="quote" label="Citation" multiline />
                </Grid>
              </Grid>
            </Dialog.Content>

            <Dialog.Actions>
              {props.onClose && (
                <Button onClick={props.onClose} variant="text">
                  Annuler
                </Button>
              )}

              <LoadingButton type="submit" loading={submitting}>
                Valider
              </LoadingButton>
            </Dialog.Actions>
          </form>
        )}
      </Form>
    </Dialog>
  );
}

ReviewFormDialog.fragments = fragments;
